import { useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { AccountSplitWeb } from '~/constants/ff.posthog';
import type { TViewedOfficeScope } from '~/types/ViewedOfficeScope';
import {
  ORGANIZATION_SCOPE,
  SELECTED_OFFICE_SCOPE,
  VIEWED_OFFICE_PARAM,
  VIEWED_SCOPE_PARAM,
} from '~/utils/viewedOfficeScope';
import { useFeatureFlagState } from './useFeatureFlagState';

/**
 * The office a page reached from Location Settings acts on, read from its URL
 * (`?officeId=<id>` or `?scope=organization`, built by `toViewedOfficePath`) so a
 * reload or a shared link keeps it. The parameters count only while
 * `GOTU-7475-account-split-web` is on; with it off the page is the selected
 * office's, as before. `undefined` while that flag is still loading for a URL that
 * carries one: hold the first fetch until it answers.
 */
export const useViewedOfficeScope = (): TViewedOfficeScope | undefined => {
  const [searchParams] = useSearchParams();
  const officeId = searchParams.get(VIEWED_OFFICE_PARAM);
  const isOrganization = searchParams.get(VIEWED_SCOPE_PARAM) === ORGANIZATION_SCOPE;
  const hasScopeParam = !!officeId || isOrganization;
  const flagStatus = useFeatureFlagState(AccountSplitWeb);

  return useMemo((): TViewedOfficeScope | undefined => {
    if (!hasScopeParam || flagStatus === 'disabled') return SELECTED_OFFICE_SCOPE;
    if (flagStatus === 'loading') return undefined;
    if (isOrganization) return { kind: 'organization' };
    return { kind: 'location', officeId: officeId as string };
  }, [flagStatus, hasScopeParam, isOrganization, officeId]);
};
